import {Image} from 'coreClasses/images/Image';
import ViewportMgr from 'coreClasses/view/ViewportMgr';

import {rebuildPathWithParams, parseURL, getAppOrigin} from 'coreUtils/url';
import {generateImagePlaceholder, getConfig} from 'coreUtils/utils';

import {USE_SVG_PLACEHOLDERS, PRODUCT_PLACEHOLDER_COLOR, ViewType} from 'brandConstants';

const breakpointsList = ViewportMgr.getBreakpoints(true);
export type TBreakpoint = (typeof breakpointsList)[number];

const FEMALE = 'F';

/**
 * Represents ProductImage
 */
class ProductImage implements Image {
	private readonly src: string;
	private readonly title: string;
	private readonly alt: string;
	private readonly placeHolderDivisor: number = 10;
	private readonly transformationObject: object | null;
	private cache = new Map<string, string>();

	/**
	 * @constructor
	 * @param {any} image - image object
	 * @param {any} transformationObject - transformation object
	 * @param {string} productHitId - product hit id
	 */
	constructor(
		image: any,
		transformationObject: any = null,
		productHitId: string = null
	) {
		if (productHitId?.split('_')[1] === FEMALE) {
			image = {...image};

			if (image.disBaseLink) {
				image.disBaseLink = image?.disBaseLink?.replace(ViewType.ZOOM, ViewType.ZOOM_FEMALE);
			}

			if (image.link) {
				image.link = image?.link?.replace(ViewType.ZOOM, ViewType.ZOOM_FEMALE);
			}
		}

		this.src = image?.disBaseLink || image?.link || '';
		this.title = image?.title || '';
		this.alt = image?.alt || '';
		this.transformationObject = transformationObject;
	}

	/**
	 * The transformation object may not contain a value for all the system breakpoints
	 * if the given breakpoint exists it is returned, otherwise we return the nearest one.
	 * @param breakPoint
	 * @private
	 */
	private findNearestAvailableBreakpoint(breakPoint: string): string {
		const systemBreakPoints = ViewportMgr.getBreakpoints(true);
		const breakPointIndex = systemBreakPoints.indexOf(breakPoint as TBreakpoint);

		for (let i = breakPointIndex + 1; i--; i >= 0) {
			const currentBreakpoint = systemBreakPoints[i];

			if (currentBreakpoint in this.transformationObject) {
				return currentBreakpoint;
			}
		}

		return Object.keys(this.transformationObject)[0];
	}

	/**
	 * Returns the url recalculated based on the given breakpoint and dpi
	 * @param {string} breakPoint the breakpoint key
	 * @param {number} dpi the desired dpi
	 * @return {string} the calculated image url
	 */
	getUrl(breakPoint: string = null, dpi: number = 1): string {
		const cacheKey = breakPoint + dpi;

		if (!this.cache.has(cacheKey)) {
			let url = this.src;

			if (this.src && this.transformationObject && breakPoint) {
				const nearestAvailableBreakpoint = this.findNearestAvailableBreakpoint(breakPoint);
				const clonedTransformationObject = Object.assign({}, this.transformationObject[nearestAvailableBreakpoint]);

				clonedTransformationObject.sw = clonedTransformationObject.sw * dpi;
				clonedTransformationObject.sh = clonedTransformationObject.sh * dpi;

				url = rebuildPathWithParams(this.src, clonedTransformationObject);
			}

			const appConfig = getConfig();
			const appOrigin = getAppOrigin();
			const isLocal = appOrigin.indexOf('https://local') !== -1;

			if (url && !appConfig.app.noVanityHostname && (appConfig.noHttpsHostInAliases || isLocal)) {
				const {pathname, search} = parseURL(url);
				const appOrigin = isLocal && appConfig.app?.noHybridHostname
					? `${appConfig.app.instanceProtocol}://${appConfig.app.noHybridHostname}`
					: getAppOrigin();

				url = `${appOrigin}${pathname}${search}`;
			}

			this.cache.set(cacheKey, url);
		}

		return this.cache.get(cacheKey);
	}

	/**
	 * Returns the placeholder url recalculated based on the given breakpoint and dpi
	 * @param {string} breakPoint the breakpoint key
	 * @param {number} dpi the desired dpi
	 * @return {string} the calculated image url
	 */
	getPlaceholderUrl(breakPoint: string = null, dpi: number = 1): string {
		if (this.transformationObject && breakPoint) {
			const nearestAvailableBreakpoint = this.findNearestAvailableBreakpoint(breakPoint);
			const clonedTransformationObject = Object.assign({}, this.transformationObject[nearestAvailableBreakpoint]);

			if (USE_SVG_PLACEHOLDERS) {
				return generateImagePlaceholder(clonedTransformationObject.sw, clonedTransformationObject.sh, PRODUCT_PLACEHOLDER_COLOR);
			} else {
				clonedTransformationObject.sw = Math.round((clonedTransformationObject.sw * dpi) / this.placeHolderDivisor);
				clonedTransformationObject.sh = Math.round((clonedTransformationObject.sh * dpi) / this.placeHolderDivisor);
			}

			return rebuildPathWithParams(this.src, clonedTransformationObject);
		} else {
			return this.src;
		}
	}

	/**
	 * Returns the image width as string
	 * @return {string} the image width
	 */
	getWidth(): string {
		if (this.transformationObject) {
			return this.transformationObject[Object.keys(this.transformationObject)[0]].sw;
		}

		return '';
	}

	/**
	 * Returns the image height as string
	 * @return {string} the image width
	 */
	getHeight(): string {
		if (this.transformationObject) {
			return this.transformationObject[Object.keys(this.transformationObject)[0]].sh;
		}

		return '';
	}

	/**
	 * Returns the image title
	 * @return {string} the image alternative text
	 */
	getTitle(): string {
		return this.title;
	}

	/**
	 * Returns the image alternative text
	 * @return {string} the image alternative text
	 */
	getAlt(): string {
		return this.alt;
	}

	/**
	 * Returns the image name
	 * @returns {string} - the image name
	 */
	getImageName(): string {
		const parts = this.src?.split('/') || [];

    	return parts.length ? parts[parts.length - 1] : '';
	}
}

export default ProductImage;
